import React from 'react';

export const OPERATOR_CHIPS: string[] = [
    '+', '-', '*', '/', '(', ')', ',', '=', '<>', '>', '>=', '<', '<=', '"YES"', '"NO"'
];

export type TestScenarioKey = 'standard';

export interface TestScenario {
    label: string;
    values: Record<string, any>;
    summary: string;
}

/** sample door the preview evaluates against; the values follow the backend logic of each variable (D / F from a "DF" layout, hinges from the height band) */
export const TEST_SCENARIOS: Record<TestScenarioKey, TestScenario> = {
    standard: {
        label: 'Standard 3000mm',
        // SQFT = H * W * 0.00001076391042, the backend value of a model that is not charged by sq.ft
        values: { H: 3000, W: 1200, SQFT: 38.750078, D: 1, F: 1, PANEL_W: 600, LEAF_SQFT: 19.375, HINGE_QTY: 5, DROP_SEAL: 'YES', DOOR_CLOSER: 'YES' },
        summary: 'H 3000 · W 1200 · sq.ft 38.75 · D 1 · F 1 · hinge 5 · drop seal YES · door closer YES'
    },
};

export const BOM_FORMULA_MAX_LENGTH = 500;

export const COMPARISON_OPERATORS: string[] = ['=', '<>', '<', '<=', '>', '>='];

export const FUNCTION_ARITY: Record<string, { min: number; max?: number }> = {
    IF: { min: 3, max: 3 },
    IFERROR: { min: 2, max: 2 },
    PICKIF: { min: 2, max: 2 },
    ROUND: { min: 1, max: 2 },
    CEIL: { min: 1, max: 1 },
    CEILING: { min: 1, max: 1 },
    FLOOR: { min: 1, max: 1 },
    MIN: { min: 1 },
    MAX: { min: 1 },
    AND: { min: 2 },
    OR: { min: 2 },
    UPPER: { min: 1, max: 1 }
};

export type BOMFormulaTokenType = 'number' | 'string' | 'identifier' | 'operator' | 'paren' | 'comma';

export interface BOMFormulaToken {
    type: BOMFormulaTokenType;
    value: string;
    position: number;
}

export type BOMFormulaNode =
    | { kind: 'number'; value: number }
    | { kind: 'text'; value: string }
    | { kind: 'variable'; name: string }
    | { kind: 'unary'; operator: string; operand: BOMFormulaNode }
    | { kind: 'binary'; operator: string; left: BOMFormulaNode; right: BOMFormulaNode }
    | { kind: 'call'; name: string; args: BOMFormulaNode[] };

export interface BOMFormulaParseResult {
    parsed?: {
        expression: string;
        variables: string[];
        functions: string[];
    };
    tree?: BOMFormulaNode;
    error?: string;
}

/** splits the expression into tokens; matches Backend bom-formula-parser.ts */
export const tokenizeBOMFormula = (expression: string): BOMFormulaToken[] => {
    const tokens: BOMFormulaToken[] = [];
    let index = 0;
    while (index < expression.length) {
        const char: string = expression[index];
        if (/\s/.test(char)) {
            index++;
            continue;
        }
        const rest: string = expression.slice(index);
        const numberMatch = rest.match(/^(\d+(\.\d+)?|\.\d+)/);
        if (numberMatch) {
            tokens.push({ type: 'number', value: numberMatch[0], position: index });
            index += numberMatch[0].length;
            continue;
        }
        const nameMatch = rest.match(/^[A-Za-z_][A-Za-z0-9_]*/);
        if (nameMatch) {
            tokens.push({ type: 'identifier', value: nameMatch[0], position: index });
            index += nameMatch[0].length;
            continue;
        }
        if (char === '"' || char === "'") {
            const end: number = expression.indexOf(char, index + 1);
            if (end === -1) {
                throw new Error(`Text opened at position ${index + 1} is never closed`);
            }
            tokens.push({ type: 'string', value: expression.slice(index + 1, end), position: index });
            index = end + 1;
            continue;
        }
        const operatorMatch = rest.match(/^(<>|<=|>=|[+\-*/=<>])/);
        if (operatorMatch) {
            tokens.push({ type: 'operator', value: operatorMatch[0], position: index });
            index += operatorMatch[0].length;
            continue;
        }
        if (char === '(' || char === ')') {
            tokens.push({ type: 'paren', value: char, position: index });
            index++;
            continue;
        }
        if (char === ',') {
            tokens.push({ type: 'comma', value: char, position: index });
            index++;
            continue;
        }
        throw new Error(`Unexpected character "${char}" at position ${index + 1}`);
    }
    return tokens;
};

/** recursive descent parser matching Backend BOMFormulaParser */
export class BOMFormulaParser {
    public readonly variables: Set<string> = new Set();
    public readonly functions: Set<string> = new Set();
    private index = 0;

    constructor(
        private readonly tokens: BOMFormulaToken[],
        private readonly allowedVariables?: string[]
    ) { }

    public parse(): BOMFormulaNode {
        const tree: BOMFormulaNode = this.parseComparison();
        const leftover = this.peek();
        if (leftover) {
            throw new Error(this.unexpected(leftover));
        }
        return tree;
    }

    private peek(): BOMFormulaToken | undefined {
        return this.tokens[this.index];
    }

    private next(): BOMFormulaToken {
        return this.tokens[this.index++];
    }

    private isOperator(values: string[]): boolean {
        const token = this.peek();
        return !!token && token.type === 'operator' && values.includes(token.value);
    }

    private isParen(value: string): boolean {
        const token = this.peek();
        return !!token && token.type === 'paren' && token.value === value;
    }

    private unexpected(token: BOMFormulaToken | undefined): string {
        return token ? `Unexpected "${token.value}" at position ${token.position + 1}` : 'Formula ends unexpectedly';
    }

    private expectParen(value: string): void {
        if (!this.isParen(value)) {
            const token = this.peek();
            throw new Error(token ? `Expected "${value}" but found "${token.value}" at position ${token.position + 1}` : `Expected "${value}" but the formula ended`);
        }
        this.index++;
    }

    private parseComparison(): BOMFormulaNode {
        let left: BOMFormulaNode = this.parseAdditive();
        while (this.isOperator(COMPARISON_OPERATORS)) {
            const operator: string = this.next().value;
            left = { kind: 'binary', operator, left, right: this.parseAdditive() };
        }
        return left;
    }

    private parseAdditive(): BOMFormulaNode {
        let left: BOMFormulaNode = this.parseTerm();
        while (this.isOperator(['+', '-'])) {
            const operator: string = this.next().value;
            left = { kind: 'binary', operator, left, right: this.parseTerm() };
        }
        return left;
    }

    private parseTerm(): BOMFormulaNode {
        let left: BOMFormulaNode = this.parseUnary();
        while (this.isOperator(['*', '/'])) {
            const operator: string = this.next().value;
            left = { kind: 'binary', operator, left, right: this.parseUnary() };
        }
        return left;
    }

    private parseUnary(): BOMFormulaNode {
        if (this.isOperator(['+', '-'])) {
            const operator: string = this.next().value;
            return { kind: 'unary', operator, operand: this.parseUnary() };
        }
        return this.parsePrimary();
    }

    private parsePrimary(): BOMFormulaNode {
        const token = this.peek();
        if (!token) {
            throw new Error(this.unexpected(token));
        }
        if (token.type === 'number') {
            this.next();
            return { kind: 'number', value: Number(token.value) };
        }
        if (token.type === 'string') {
            this.next();
            return { kind: 'text', value: token.value };
        }
        if (token.type === 'paren' && token.value === '(') {
            this.next();
            const inner: BOMFormulaNode = this.parseComparison();
            this.expectParen(')');
            return inner;
        }
        if (token.type === 'identifier') {
            this.next();
            return this.isParen('(') ? this.parseCall(token) : this.parseVariable(token);
        }
        throw new Error(this.unexpected(token));
    }

    private parseVariable(token: BOMFormulaToken): BOMFormulaNode {
        if (this.allowedVariables && this.allowedVariables.length > 0 && !this.allowedVariables.includes(token.value)) {
            throw new Error(`Unknown name "${token.value}" at position ${token.position + 1}`);
        }
        this.variables.add(token.value);
        return { kind: 'variable', name: token.value };
    }

    private parseCall(token: BOMFormulaToken): BOMFormulaNode {
        const name: string = token.value.toUpperCase();
        const arity = FUNCTION_ARITY[name];
        if (!arity) {
            throw new Error(`Unknown function "${token.value}" at position ${token.position + 1}`);
        }
        this.expectParen('(');
        const args: BOMFormulaNode[] = [];
        if (!this.isParen(')')) {
            args.push(this.parseComparison());
            let separator = this.peek();
            while (separator && separator.type === 'comma') {
                this.next();
                args.push(this.parseComparison());
                separator = this.peek();
            }
        }
        this.expectParen(')');
        if (args.length < arity.min || (arity.max !== undefined && args.length > arity.max)) {
            const expected: string = arity.max === undefined
                ? `at least ${arity.min}`
                : arity.min === arity.max ? `${arity.min}` : `${arity.min} to ${arity.max}`;
            throw new Error(`${name} expects ${expected} argument(s) but got ${args.length}`);
        }
        this.functions.add(name);
        return { kind: 'call', name, args };
    }
}

/** checks if the resulting AST yields text instead of number */
const resultTypeOf = (node: BOMFormulaNode): 'number' | 'text' => {
    switch (node.kind) {
        case 'text':
            return 'text';
        case 'call':
            if (node.name === 'UPPER') {
                return 'text';
            }
            if (node.name === 'IF') {
                return resultTypeOf(node.args[1]) === 'text' || resultTypeOf(node.args[2]) === 'text' ? 'text' : 'number';
            }
            if (node.name === 'IFERROR') {
                return node.args.some((arg) => resultTypeOf(arg) === 'text') ? 'text' : 'number';
            }
            if (node.name === 'PICKIF') {
                return resultTypeOf(node.args[1]);
            }
            return 'number';
        default:
            return 'number';
    }
};

/** parse and validate a BOM formula expression; optionally checks against backend-provided allowedVariables */
export const parseBOMFormula = (expression: string, allowedVariables?: string[]): BOMFormulaParseResult => {
    const trimmed: string = typeof expression === 'string' ? expression.trim() : '';
    if (trimmed.length === 0) {
        return { error: 'Formula is empty' };
    }
    if (trimmed.length > BOM_FORMULA_MAX_LENGTH) {
        return { error: `Formula must be at most ${BOM_FORMULA_MAX_LENGTH} characters` };
    }
    try {
        const parser = new BOMFormulaParser(tokenizeBOMFormula(trimmed), allowedVariables);
        const tree: BOMFormulaNode = parser.parse();
        if (resultTypeOf(tree) === 'text') {
            return { error: 'Formula must give a number, not text' };
        }
        const functions: string[] = Array.from(parser.functions);
        const variables: string[] = Array.from(parser.variables);
        return { parsed: { expression: trimmed, variables, functions }, tree };
    } catch (error) {
        return { error: error instanceof Error ? error.message : String(error) };
    }
};

type BOMFormulaValue = number | string | boolean;

/** thrown by PICKIF when its condition does not hold, as Backend bom-formula-calculator.ts does: the CS code is not picked for the door */
const NOT_PICKED = new Error('Not picked');

const toFormulaNumber = (value: BOMFormulaValue, what: string): number => {
    const parsed = typeof value === 'boolean' ? (value ? 1 : 0) : typeof value === 'string' ? Number(value) : value;
    if (typeof parsed !== 'number' || Number.isNaN(parsed)) {
        throw new Error(`${what} is not a number`);
    }
    return parsed;
};

const isFormulaTruthy = (value: BOMFormulaValue): boolean => {
    return typeof value === 'string' ? value !== '' : !!value;
};

const isFormulaEqual = (left: BOMFormulaValue, right: BOMFormulaValue): boolean => {
    return typeof left === 'string' || typeof right === 'string'
        ? String(left) === String(right)
        : toFormulaNumber(left, 'Left side of =') === toFormulaNumber(right, 'Right side of =');
};

const roundToDecimals = (value: number, digits: number): number => {
    const factor: number = Math.pow(10, Math.max(0, Math.trunc(digits)));
    return Math.round(value * factor) / factor;
};

const evaluateFormulaNode = (node: BOMFormulaNode, values: Record<string, any>): BOMFormulaValue => {
    switch (node.kind) {
        case 'number':
        case 'text':
            return node.value;
        case 'variable':
            if (values[node.name] === undefined || values[node.name] === null) {
                throw new Error(`No value for "${node.name}"`);
            }
            return values[node.name];
        case 'unary': {
            const operand: number = toFormulaNumber(evaluateFormulaNode(node.operand, values), `Operand of ${node.operator}`);
            return node.operator === '-' ? -operand : operand;
        }
        case 'binary': {
            const left: BOMFormulaValue = evaluateFormulaNode(node.left, values);
            const right: BOMFormulaValue = evaluateFormulaNode(node.right, values);
            switch (node.operator) {
                case '=': return isFormulaEqual(left, right);
                case '<>': return !isFormulaEqual(left, right);
            }
            const a: number = toFormulaNumber(left, `Left side of ${node.operator}`);
            const b: number = toFormulaNumber(right, `Right side of ${node.operator}`);
            switch (node.operator) {
                case '+': return a + b;
                case '-': return a - b;
                case '*': return a * b;
                case '/':
                    if (b === 0) throw new Error('Division by zero');
                    return a / b;
                case '<': return a < b;
                case '<=': return a <= b;
                case '>': return a > b;
                default: return a >= b;
            }
        }
        case 'call': {
            const argument = (index: number): BOMFormulaValue => evaluateFormulaNode(node.args[index], values);
            const numberAt = (index: number): number => toFormulaNumber(argument(index), `Argument ${index + 1} of ${node.name}`);
            switch (node.name) {
                case 'IF':
                    return isFormulaTruthy(argument(0)) ? argument(1) : argument(2);
                case 'IFERROR':
                    try {
                        const val: BOMFormulaValue = argument(0);
                        return typeof val === 'number' && !Number.isFinite(val) ? argument(1) : val;
                    } catch (error) {
                        if (error === NOT_PICKED) {
                            throw error;
                        }
                        return argument(1);
                    }
                case 'ROUND':
                    return roundToDecimals(numberAt(0), node.args.length > 1 ? numberAt(1) : 0);
                case 'CEIL':
                case 'CEILING':
                    return Math.ceil(numberAt(0));
                case 'FLOOR':
                    return Math.floor(numberAt(0));
                case 'MIN':
                    return Math.min(...node.args.map((_, i) => numberAt(i)));
                case 'MAX':
                    return Math.max(...node.args.map((_, i) => numberAt(i)));
                case 'AND':
                    return node.args.every((_, i) => isFormulaTruthy(argument(i)));
                case 'OR':
                    return node.args.some((_, i) => isFormulaTruthy(argument(i)));
                case 'PICKIF':
                    if (!isFormulaTruthy(argument(0))) {
                        throw NOT_PICKED;
                    }
                    return argument(1);
                default:
                    return String(argument(0)).toUpperCase();
            }
        }
    }
};

/**
 * Gives the number as text, '-' for an empty formula, 'Not picked' when a PICKIF condition does not hold for the sample door,
 * 'Syntax error' when it cannot be evaluated and 'Not a number' when the result is text or not finite.
 */
export const safeEvaluateFormula = (formula: string, ctx: Record<string, any>, allowedVariables?: string[]): string => {
    if (!formula || !formula.trim()) return '-';
    const vars = allowedVariables && allowedVariables.length > 0 ? allowedVariables : (ctx ? Object.keys(ctx) : undefined);
    const parseResult: BOMFormulaParseResult = parseBOMFormula(formula, vars);
    if (!parseResult.tree) {
        return parseResult.error === 'Formula must give a number, not text' ? 'Not a number' : 'Syntax error';
    }
    try {
        const raw: BOMFormulaValue = evaluateFormulaNode(parseResult.tree, ctx);
        const numeric: number | boolean | string = typeof raw === 'boolean' ? (raw ? 1 : 0) : raw;
        if (typeof numeric !== 'number' || !Number.isFinite(numeric)) {
            return 'Not a number';
        }
        return String(Math.round(numeric * 1000) / 1000);
    } catch (error) {
        if (error === NOT_PICKED) {
            return 'Not picked';
        }
        return 'Syntax error';
    }
};

export interface PillChipProps {
    label: string;
    onClick: () => void;
    /** shown by the page's Tooltip on hover; a line break separates the name from how the value is found */
    tooltip?: string;
    variant?: 'variable' | 'operator';
    /** the formula already uses this token */
    active?: boolean;
}

export const PillChip: React.FC<PillChipProps> = ({
    label,
    onClick,
    tooltip,
    variant = 'variable',
    active = false
}) => {
    const className = `bom-token-chip ${variant}${active ? ' in-use' : ''}`;
    return React.createElement(
        'button',
        { type: 'button', className, onClick, 'data-pr-tooltip': tooltip, 'data-pr-position': 'top' },
        label
    );
};
